import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Mail, MoreHorizontal, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { ActionPopover } from '@/components/action-popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CallsPanel } from '@/features/phone-calls/calls-panel'
import { BackButton } from '@/components/back-button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { SearchSelect } from '@/components/search-select'
import { SettingCell } from '@/components/setting-cell'
import { StatusPill } from '@/components/status-pill'
import { getApiErrorMessage } from '@/lib/api-error'
import { formatContactRole } from '@/lib/contact-roles'
import { usePageTitle } from '@/lib/page-title'
import { formatPhone } from '@/lib/phone'
import {
  ACCOUNT_LABELS,
  ACCOUNT_TONES,
  SUPPLIER_KIND_TONES,
  SUPPLIER_ROLES,
  SUPPLIER_ROLE_LABELS,
  accountStatus,
  termsPill,
} from '@/lib/suppliers'
import { cn, formatStatus } from '@/lib/utils'
import {
  SUPPLIER_KIND_LABELS,
  supplierCoverage,
  useAddSupplierContact,
  useDeleteSupplier,
  useEditSupplier,
  useMergeSupplier,
  useRemoveSupplierContact,
  useSupplier,
  useSuppliers,
  type SupplierDetail,
  type SupplierKind,
  type SupplierSourcing,
} from '@/api/sourcing'

const RANK_FILTERS = [
  { value: 'preferred', label: 'Preferred source' },
  { value: 'backup', label: 'Backup source' },
  { value: 'all', label: 'All' },
] as const

type RankFilter = (typeof RANK_FILTERS)[number]['value']

type EditableField = 'name' | 'kind' | 'email_domain' | 'address1' | 'city' | 'state' | 'zip_code' | 'phone'

function sourceRole(row: SupplierSourcing, supplier: SupplierDetail) {
  if (row.buying_group_id === supplier.id && row.supplier_id !== supplier.id) return 'Buying group'
  return row.rank === 1 ? 'Preferred source' : `Backup source ${row.rank}`
}

function matchesRank(row: SupplierSourcing, filter: RankFilter) {
  if (filter === 'all') return true
  return filter === 'preferred' ? row.rank === 1 : row.rank > 1
}

function supplierEmail(supplier: SupplierDetail, role: string) {
  return supplier.contacts?.find((person) => person.role === role)?.contact.email?.toLowerCase()
}

/** Who a manufacturer's POs and spec credit requests go to; a manufacturer not using the supplier's people stands out. */
function Recipients({ row, supplier }: { row: SupplierSourcing; supplier: SupplierDetail }) {
  const orders = supplierEmail(supplier, 'po_primary')
  const spec = supplierEmail(supplier, 'spec_credit')
  const poTo = [row.po_email, ...(row.po_cc_emails ?? [])].map((email) => email?.toLowerCase())
  const ordersMissing = row.rank === 1 && !!orders && !poTo.includes(orders)
  const specOff = row.rank === 1 && !!spec && row.spec_credit_email?.toLowerCase() !== spec
  return (
    <div className="space-y-0.5 text-xs">
      <p className="break-words">
        <span className="text-muted-foreground">POs </span>
        {row.po_email ?? <span className="text-amber-700 dark:text-amber-300">no recipient</span>}
        {(row.po_cc_emails ?? []).length > 0 && (
          <span className="text-muted-foreground"> cc {(row.po_cc_emails ?? []).join(', ')}</span>
        )}
      </p>
      <p className="break-words">
        <span className="text-muted-foreground">Spec credit </span>
        {row.spec_credit_email ?? <span className="text-amber-700 dark:text-amber-300">no recipient</span>}
        {row.spec_credit_from_po_contact && <span className="text-muted-foreground"> (PO contact)</span>}
      </p>
      {(ordersMissing || specOff) && (
        <StatusPill tone="warning" className="mt-1">
          {ordersMissing ? `POs skip ${supplier.name}` : `Spec credit not to ${spec}`}
        </StatusPill>
      )}
    </div>
  )
}

function ManufacturerCell({ row, supplier, showSource }: { row: SupplierSourcing; supplier: SupplierDetail; showSource: boolean }) {
  const status = row.manufacturer.status
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <Link
          to="/manufacturers/$manuId"
          params={{ manuId: row.manufacturer.uuid }}
          className="font-medium text-primary hover:underline"
        >
          {row.manufacturer.name}
        </Link>
        {status && (
          <StatusPill tone={status === 'preferred' ? 'success' : 'progress'}>{formatStatus(status)}</StatusPill>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {[
          showSource ? sourceRole(row, supplier) : null,
          row.product_scope ? `${row.product_scope} only` : null,
          [row.manufacturer.city, row.manufacturer.state].filter(Boolean).join(', '),
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </div>
  )
}

/** Account status and (for the preferred source) payment terms, stacked. */
function AccountTerms({ row }: { row: SupplierSourcing }) {
  const status = accountStatus(row.account_status)
  const terms = row.rank === 1 ? termsPill(row.manufacturer) : null
  return (
    <div className="flex flex-wrap items-center gap-1 lg:flex-col lg:items-start">
      <StatusPill tone={ACCOUNT_TONES[status]}>
        {ACCOUNT_LABELS[status]}
        {row.account_number ? ` #${row.account_number}` : ''}
      </StatusPill>
      {terms && <StatusPill tone={terms.tone}>{terms.label}</StatusPill>}
    </div>
  )
}

function ManufacturersTab({ supplier }: { supplier: SupplierDetail }) {
  const buyingGroup = supplier.kind === 'buying_group'
  const [filter, setFilter] = useState<RankFilter>(
    buyingGroup || !supplier.preferred_for ? 'all' : 'preferred',
  )
  const rows = (supplier.sourcing ?? []).filter((row) => matchesRank(row, filter))
  const showSource = filter === 'all' && !buyingGroup

  return (
    <div className="space-y-3">
      {!buyingGroup && (
        <Select value={filter} onValueChange={(value) => setFilter(value as RankFilter)}>
          <SelectTrigger aria-label="Which sources" className="w-full sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANK_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {!rows.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No manufacturers</p>
      ) : (
        <>
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Account & terms</TableHead>
                  <TableHead>Emails go to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="align-top">
                    <TableCell className="max-w-[300px] whitespace-normal">
                      <ManufacturerCell row={row} supplier={supplier} showSource={showSource} />
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <AccountTerms row={row} />
                    </TableCell>
                    <TableCell className="max-w-[380px] whitespace-normal">
                      <Recipients row={row} supplier={supplier} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-2 lg:hidden">
            {rows.map((row) => (
              <div key={row.id} className="space-y-2 rounded-lg border bg-card p-3">
                <ManufacturerCell row={row} supplier={supplier} showSource={showSource} />
                <AccountTerms row={row} />
                <Recipients row={row} supplier={supplier} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** The supplier's people by role; every manufacturer bought through it uses them. */
function ContactsPanel({ supplier }: { supplier: SupplierDetail }) {
  const add = useAddSupplierContact(supplier.uuid)
  const remove = useRemoveSupplierContact(supplier.uuid)
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('po_primary')
  const contacts = supplier.contacts ?? []
  const roles = SUPPLIER_ROLES.filter((value) => contacts.some((person) => person.role === value))
  const reach = (supplier.preferred_for ?? 0) + (supplier.backup_for ?? 0)

  const submit = () => {
    if (!email.trim()) return
    add.mutate(
      { supplierId: supplier.id, email: email.trim(), role: role as 'po_primary' },
      {
        onSuccess: () => {
          setEmail('')
          setOpen(false)
        },
        onError: (error) => toast.error(getApiErrorMessage(error, `Could not add ${email}`)),
      },
    )
  }

  return (
    <section aria-labelledby="supplier-contacts" className="rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
        <h2 id="supplier-contacts" className="text-sm">
          <span className="font-semibold">Contacts</span>
          {reach > 0 && (
            <span className="text-muted-foreground">
              {' '}
              · used for all {reach} {reach === 1 ? 'manufacturer' : 'manufacturers'}
            </span>
          )}
        </h2>
        <ActionPopover
          open={open}
          onOpenChange={setOpen}
          align="end"
          title="Add contact"
          trigger={
            <Button size="sm" variant="ghost" className="-mr-2">
              <Plus className="size-4" /> Add
            </Button>
          }
          confirmLabel="Add"
          pending={add.isPending}
          pendingLabel="Adding..."
          onConfirm={submit}
        >
          <InlineFieldInput
            type="email"
            placeholder="name@company.com"
            aria-label="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger aria-label="Role" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPLIER_ROLES.map((value) => (
                <SelectItem key={value} value={value}>
                  {SUPPLIER_ROLE_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ActionPopover>
      </div>
      {!roles.length && (
        <p className="border-b px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          No contacts yet: each manufacturer still uses its own.
        </p>
      )}
      <dl className="divide-y">
          {roles.map((value) => (
            <div key={value} className="grid gap-1 px-4 py-2.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center">
              <dt className="text-xs font-medium text-muted-foreground">{SUPPLIER_ROLE_LABELS[value]}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {contacts
                  .filter((person) => person.role === value)
                  .map((person) => (
                    <span
                      key={person.id}
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-0.5 pl-2.5 pr-1 text-sm"
                    >
                      <span className="truncate">{person.contact.name || person.contact.email}</span>
                      <LoadingButton
                        variant="ghost"
                        size="icon"
                        className="size-5 rounded-full"
                        aria-label={`Remove ${person.contact.email} from ${SUPPLIER_ROLE_LABELS[value]}`}
                        loading={remove.isPending && remove.variables?.contactId === person.id}
                        onClick={() =>
                          remove.mutate(
                            { supplierId: supplier.id, contactId: person.id },
                            {
                              onError: (error) =>
                                toast.error(getApiErrorMessage(error, `Could not remove ${person.contact.email}`)),
                            },
                          )
                        }
                      >
                        <X className="size-3" />
                      </LoadingButton>
                    </span>
                  ))}
              </dd>
            </div>
          ))}
        <SpecCreditRuleRow supplier={supplier} />
      </dl>
    </section>
  )
}

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/** The typed minimum in whole dollars, as the rule shows it; anything unreadable as typed. */
function amountLabel(amount: string): string {
  const value = Number(amount.replace(/[$,\s]/g, ''))
  return amount.trim() && Number.isFinite(value) ? wholeDollars.format(value) : amount
}

/** Whether the supplier's brands ask spec credit on their own or once per project above a minimum (USA-1553). */
function SpecCreditRuleRow({ supplier }: { supplier: SupplierDetail }) {
  const edit = useEditSupplier(supplier.uuid)
  const minimum = supplier.spec_credit_rule?.project_minimum
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const pending = edit.isPending && edit.variables?.spec_credit_project_minimum !== undefined

  const save = (value: string | null) =>
    edit.mutate(
      { id: supplier.id, spec_credit_project_minimum: value },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => toast.error(getApiErrorMessage(error, `Could not save ${supplier.name}`)),
      },
    )
  const saveAmount = () => {
    const value = Number(amount.replace(/[$,\s]/g, ''))
    if (!Number.isFinite(value) || value < 0) return
    save(value.toFixed(2))
  }

  return (
    <div className="grid gap-1 px-4 py-2.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center">
      <dt className="text-xs font-medium text-muted-foreground">Spec credit asks</dt>
      <dd>
        <ActionPopover
          open={open}
          onOpenChange={(next) => {
            setOpen(next)
            if (next) setAmount(minimum != null ? String(Math.round(Number(minimum))) : '8000')
          }}
          title="Spec credit asks"
          trigger={
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-auto gap-1.5 px-2 py-0.5 text-sm font-medium"
              aria-label="Edit how spec credit is asked"
            >
              {minimum != null ? (
                <>
                  Once per project ·
                  <StatusPill tone="success">{wholeDollars.format(Number(minimum))} and up</StatusPill>
                </>
              ) : (
                <span className="font-normal text-muted-foreground">Each brand on its own</span>
              )}
            </Button>
          }
          confirmLabel="Save"
          pending={pending}
          pendingLabel="Saving..."
          onConfirm={saveAmount}
        >
          <button
            type="button"
            className={cn(
              'flex w-full rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted',
              minimum == null && 'bg-muted font-medium',
            )}
            onClick={() => (minimum == null ? setOpen(false) : save(null))}
          >
            Each brand on its own
          </button>
          <div
            className={cn(
              'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm',
              minimum != null && 'bg-muted font-medium',
            )}
            // Enter sets the amount; Save below sets the rule.
            onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
          >
            <span className="shrink-0">Once per project, at</span>
            {/* Click to change the amount; Save below sets the rule (USA-1580). */}
            <POInlineEditableField
              kind="money"
              ariaLabel="Project minimum"
              value={amount}
              displayValue={amountLabel(amount)}
              placeholder="Not set"
              buttonClassName="min-w-20 tabular-nums"
              inputClassName="w-24 tabular-nums"
              onConfirm={setAmount}
            />
            <span className="shrink-0">and up</span>
          </div>
        </ActionPopover>
      </dd>
    </div>
  )
}

function PeopleTab({ supplier }: { supplier: SupplierDetail }) {
  const people = supplier.people ?? []
  if (!people.length) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No people on file</p>
  }
  return (
    <ul className="divide-y rounded-lg border bg-card">
      {people.map((person) => (
        <li
          key={person.contact.id}
          className="grid gap-1 px-3 py-2.5 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-4"
        >
          <div className="min-w-0">
            {person.contact.name && <p className="truncate font-medium">{person.contact.name}</p>}
            {/* eslint-disable-next-line no-restricted-syntax -- mailto link */}
            <a
              href={`mailto:${person.contact.email}`}
              className={cn(
                'inline-flex max-w-full items-center gap-1 text-primary hover:underline',
                person.contact.name ? 'text-xs' : 'font-medium',
              )}
            >
              <Mail className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{person.contact.email}</span>
            </a>
          </div>
          <div className="min-w-0 space-y-1 text-xs">
            {person.roles?.length ? (
              <div className="flex flex-wrap gap-1">
                {person.roles.map((role) => (
                  <StatusPill key={role} tone="neutral">
                    {formatContactRole(role)}
                  </StatusPill>
                ))}
              </div>
            ) : null}
            {person.manufacturers?.length ? (
              <p>
                {person.manufacturers.map((manufacturer, index) => (
                  <span key={manufacturer.id}>
                    {index > 0 && ', '}
                    <Link
                      to="/manufacturers/$manuId"
                      params={{ manuId: manufacturer.uuid }}
                      className="text-primary hover:underline"
                    >
                      {manufacturer.name}
                    </Link>
                  </span>
                ))}
              </p>
            ) : (
              <p className="text-muted-foreground">Not linked to a manufacturer</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

function MergeDialog({
  supplier,
  open,
  onOpenChange,
}: {
  supplier: SupplierDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { data: suppliers } = useSuppliers()
  const merge = useMergeSupplier()
  const [intoId, setIntoId] = useState<string | undefined>()
  const options = (suppliers ?? [])
    .filter((other) => other.kind === supplier.kind && other.id !== supplier.id)
    .map((other) => ({
      value: String(other.id),
      label: other.name,
      description: [other.email_domain, supplierCoverage(other)].filter(Boolean).join(' · '),
    }))
  const target = (suppliers ?? []).find((other) => String(other.id) === intoId)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setIntoId(undefined)
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge {supplier.name}</DialogTitle>
          <DialogDescription>
            Its manufacturers and people move to the supplier you pick, then {supplier.name} is deleted.
          </DialogDescription>
        </DialogHeader>
        <SearchSelect
          value={intoId}
          options={options}
          onChange={setIntoId}
          placeholder={`Choose a ${SUPPLIER_KIND_LABELS[supplier.kind].toLowerCase()}`}
          searchPlaceholder="Search suppliers"
          ariaLabel="Merge into"
          className="h-9 w-full"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <LoadingButton
            disabled={!target}
            loading={merge.isPending}
            loadingText="Merging..."
            onClick={() => {
              if (!target) return
              merge.mutate(
                { id: supplier.id, intoId: target.id },
                {
                  onSuccess: (merged) => {
                    toast.success(`${supplier.name} merged into ${merged.name}`)
                    onOpenChange(false)
                    void navigate({ to: '/suppliers/$supplierId', params: { supplierId: merged.uuid } })
                  },
                  onError: (error) =>
                    toast.error(
                      getApiErrorMessage(error, `Could not merge ${supplier.name}`),
                    ),
                },
              )
            }}
          >
            Merge
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteDialog({
  supplier,
  open,
  onOpenChange,
}: {
  supplier: SupplierDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const remove = useDeleteSupplier()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {supplier.name}?</DialogTitle>
          <DialogDescription>No manufacturer buys through it.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <LoadingButton
            variant="destructive"
            loading={remove.isPending}
            loadingText="Deleting..."
            onClick={() =>
              remove.mutate(supplier.id, {
                onSuccess: () => {
                  toast.success(`${supplier.name} deleted`)
                  void navigate({ to: '/suppliers' })
                },
                onError: (error) =>
                  toast.error(getApiErrorMessage(error, `Could not delete ${supplier.name}`)),
              })
            }
          >
            Delete
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const KIND_OPTIONS = (Object.keys(SUPPLIER_KIND_LABELS) as SupplierKind[]).map((kind) => ({
  value: kind,
  label: SUPPLIER_KIND_LABELS[kind],
}))

function SupplierSettings({ supplier }: { supplier: SupplierDetail }) {
  const edit = useEditSupplier(supplier.uuid)
  const pendingField =
    edit.isPending && edit.variables
      ? (Object.keys(edit.variables).find((key) => key !== 'id') as EditableField | undefined)
      : undefined

  const save = (field: EditableField, raw: string) => {
    const value = raw.trim() || null
    if ((field === 'name' || field === 'kind') && !value) return
    edit.mutate(
      { id: supplier.id, [field]: value },
      {
        onError: (error) =>
          toast.error(getApiErrorMessage(error, `Could not save ${supplier.name}`)),
      },
    )
  }

  const field = (key: EditableField, label: string, value: string | null | undefined, className?: string) => (
    <SettingCell label={label} className={className}>
      <POInlineEditableField
        value={value ?? ''}
        displayValue={key === 'phone' && value ? formatPhone(value) : undefined}
        placeholder="Not set"
        ariaLabel={`Edit ${label.toLowerCase()}`}
        pending={pendingField === key}
        showEditIcon
        allowWrap
        buttonClassName={cn('-ml-2 font-medium', !value && 'font-normal text-muted-foreground')}
        onConfirm={(raw) => save(key, raw)}
      />
    </SettingCell>
  )

  return (
    <section
      aria-label="Supplier details"
      className="grid grid-cols-2 overflow-hidden rounded-xl border border-b-0 border-r-0 bg-card lg:grid-cols-4"
    >
      {field('name', 'Name', supplier.name)}
      <SettingCell label="Kind">
        <POInlineEditableField
          kind="select"
          value={supplier.kind}
          displayValue={SUPPLIER_KIND_LABELS[supplier.kind]}
          options={KIND_OPTIONS}
          ariaLabel="Edit kind"
          pending={pendingField === 'kind'}
          buttonClassName="-ml-2 font-medium"
          onConfirm={(raw) => save('kind', raw)}
        />
      </SettingCell>
      {field('email_domain', 'Email domain', supplier.email_domain, 'col-span-2 lg:col-span-1')}
      {field('phone', 'Phone', supplier.address?.phone, 'col-span-2 lg:col-span-1')}
      {field('address1', 'Street', supplier.address?.address1)}
      {field('city', 'City', supplier.address?.city)}
      {field('state', 'State', supplier.address?.state)}
      {field('zip_code', 'ZIP', supplier.address?.zip_code)}
    </section>
  )
}

export function SupplierDetailPage({ supplierId }: { supplierId: string }) {
  const { data: supplier, isLoading, error } = useSupplier(supplierId)
  const [tab, setTab] = useState('manufacturers')
  const [mergeOpen, setMergeOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  usePageTitle(supplier?.name)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4" role="status" aria-label="Loading supplier">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (error || !supplier) {
    return (
      <div className="mx-auto max-w-7xl space-y-2">
        <BackButton fallback={{ to: '/suppliers' }} fallbackLabel="Back to Suppliers" />
        <p className="py-12 text-center text-muted-foreground">Supplier not found</p>
      </div>
    )
  }

  const inUse = !!(supplier.preferred_for || supplier.backup_for || supplier.buying_group_for)
  const place = [
    supplier.email_domain,
    supplier.address?.phone ? formatPhone(supplier.address.phone) : null,
    [supplier.address?.city, supplier.address?.state].filter(Boolean).join(', '),
  ].filter(Boolean)

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <header>
        <BackButton fallback={{ to: '/suppliers' }} fallbackLabel="Back to Suppliers" />
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold tracking-tight">{supplier.name}</h1>
              <StatusPill tone={SUPPLIER_KIND_TONES[supplier.kind]}>{SUPPLIER_KIND_LABELS[supplier.kind]}</StatusPill>
            </div>
            <p className="text-sm text-muted-foreground">
              {[supplierCoverage(supplier), ...place].join(' · ')}
            </p>
          </div>
          <PickerMenu open={actionsOpen} onOpenChange={setActionsOpen}>
            <PickerMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Supplier actions" className="shrink-0">
                <MoreHorizontal className="size-4" />
              </Button>
            </PickerMenuTrigger>
            <PickerMenuContent title={supplier.name} titleOnPhoneOnly align="end">
              <PickerMenuList>
                <PickerMenuItem
                  onSelect={() => {
                    setActionsOpen(false)
                    setMergeOpen(true)
                  }}
                >
                  Merge into another supplier
                </PickerMenuItem>
                <PickerMenuItem
                  disabled={inUse}
                  onSelect={() => {
                    setActionsOpen(false)
                    setDeleteOpen(true)
                  }}
                  className="text-destructive"
                >
                  Delete
                </PickerMenuItem>
              </PickerMenuList>
            </PickerMenuContent>
          </PickerMenu>
        </div>
      </header>

      {supplier.kind !== 'buying_group' && <ContactsPanel supplier={supplier} />}

      <Tabs value={tab} onValueChange={setTab} className="pt-2">
        <TabsList variant="line" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="manufacturers">Manufacturers</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="calls">Calls</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>
        <TabsContent value="manufacturers" className="mt-4">
          <ManufacturersTab supplier={supplier} />
        </TabsContent>
        <TabsContent value="people" className="mt-4">
          <PeopleTab supplier={supplier} />
        </TabsContent>
        <TabsContent value="calls" className="mt-4">
          <CallsPanel params={{ supplier_id: supplier.id }} />
        </TabsContent>
        <TabsContent value="details" className="mt-4">
          <SupplierSettings supplier={supplier} />
        </TabsContent>
      </Tabs>

      <MergeDialog supplier={supplier} open={mergeOpen} onOpenChange={setMergeOpen} />
      <DeleteDialog supplier={supplier} open={deleteOpen} onOpenChange={setDeleteOpen} />
    </div>
  )
}
